// eslint-disable-next-line eslint-comments/disable-enable-pair -- Enable
/* eslint-disable react/jsx-key -- Keys not required here */
import React from 'react';
import ModuleRoute from 'holocron-module-route';
import { setDevFlags as setDevFlagsScoped } from '@americanexpress/conditional-features';
import { IndexRedirect } from '@americanexpress/one-app-router';
import { generateErrorReportingBrowserSessionUUID } from '@americanexpress/travel-error-base';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import requireAuthForVrHook from './hooks/requireAuthForVr';
import { akamaiRedirectHook } from './hooks/akamaiRedirectHook';
import { localeSpecificRedirectHook } from './hooks/localeSpecificRedirectHook';
import Partial, { onPartialRouteEnter } from './components/Partial/Partial';
import generateDevFlagConfig from './devFlags/generateDevFlagConfig';
import ErrorLayout from './components/ErrorHandling/ErrorLayout';
import ErrorMessage from './components/ErrorHandling/ErrorMessage';
import LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER from './constants/routeConstants';
import onEnterLoadIntl from './routes/onEnterLoadIntl';
import onEnterSetLocaleFromRoutePath from './routes/onEnterSetLocaleFromRoutePath';
import { addTealeafIdToErrorReporting } from './thunks/tealeafThunks';
import { CRUISES_SUPPORTED_LOCALES, PACKAGES_SUPPORTED_LOCALES, VACATION_RENTALS_SUPPORTED_LOCALES } from './constants/supportedLocalesConstants';
import getSafeIndexRedirectLocaleUtil from './utils/getSafeIndexRedirectLocaleUtil';
import { validatedSessionDestPath } from './hooks/validateSessionDestPath';
import ClickToChat from './components/ClickToChat/ClickToChat';
import onEnterAuthenticate from './routes/onEnterAuthenticate';

const childRoutes = (store) => {
  const state = store.getState();
  const devFlagConfig = generateDevFlagConfig(state);
  if (global.BROWSER) {
    store.dispatch(addTealeafIdToErrorReporting);
  }
  const activeLocale = getActiveLocaleSelector(state);
  try {
    store.dispatch(generateErrorReportingBrowserSessionUUID);
  } catch (e) {
    // putting this here to prevent the app from crashing if the error reporter session data fails
  }
  setDevFlagsScoped(devFlagConfig);
  return [
    <ModuleRoute path="demo/:moduleName" moduleName="axp-demo" onEnter={onEnterAuthenticate(store)} />,
    <ModuleRoute path="partials(/:locale)/:moduleName/:version" component={Partial} onEnter={onPartialRouteEnter(store, true)} />,
    <ModuleRoute onEnter={onEnterSetLocaleFromRoutePath(store)} path="/">
      <ModuleRoute moduleName="axp-travel-page-wrapper" onEnter={onEnterAuthenticate(store)}>
        <IndexRedirect to={`${getSafeIndexRedirectLocaleUtil(activeLocale).toLowerCase()}/travel`} />
        <ModuleRoute
          path="(:locale)/travel"
          moduleName="axp-travel-home"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="home"
          moduleName="axp-travel-home"
          onEnter={akamaiRedirectHook(store, 'en-us/travel')}
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="flight"
          moduleName="axp-travel-home"
          onEnter={akamaiRedirectHook(store, 'en-us/travel')}
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/flights"
          moduleName="axp-travel-home"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/hotels"
          moduleName="axp-travel-home"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute onEnter={localeSpecificRedirectHook(VACATION_RENTALS_SUPPORTED_LOCALES)}>
          <ModuleRoute
            path="(:locale)/travel/vacation-rentals"
            moduleName="axp-travel-home"
            onEnter={requireAuthForVrHook(store)}
            title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
          />
          <ModuleRoute
            path="(:locale)/travel/guestbook"
            moduleName="axp-travel-home"
            title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
          />
          <ModuleRoute
            path="(:locale)/travel/vacation-packages"
            moduleName="axp-travel-home"
            title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
          />
        </ModuleRoute>
        <ModuleRoute
          path="(:locale)/travel/packages"
          moduleName="axp-travel-home"
          onEnter={localeSpecificRedirectHook(PACKAGES_SUPPORTED_LOCALES)}
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/cars"
          moduleName="axp-travel-home"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/cruises"
          moduleName="axp-travel-home"
          onEnter={localeSpecificRedirectHook(CRUISES_SUPPORTED_LOCALES)}
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/international-airline-program"
          moduleName="axp-travel-international-airline-program"
          title="Book Business & First Class International Flights | American Express Travel"
        />
        <ModuleRoute
          path="(:locale)/travel/platinum-member-airfares/"
          moduleName="axp-travel-platinum-member-airfares"
          title="Platinum Member Airfares"
        />
        <ModuleRoute
          path="(:locale)/travel/offers/flights/member-week-flight-deals-and-offers"
          moduleName="axp-travel-international-airline-program"
          title="Flight Deals & Offers | Member Week | American Express Travel"
        />
        <ModuleRoute
          path="(:locale)/travel/flights/lower-airfares"
          moduleName="axp-travel-campaign-fares"
          title="Lower Airfares & Flight Deals | American Express Travel"
          taxonomy="lower-airfares"
        />
        <ModuleRoute
          path="(:locale)/travel/insider-fares"
          moduleName="axp-travel-campaign-fares"
          title="Insider Fares: Book with Membership Rewards Points - American Express US"
          taxonomy="insider-fares"
        />
        <ModuleRoute
          path="(:locale)/travel/benefits/how-to-use-hotel-credit"
          moduleName="axp-travel-credit"
          title="How to Use Your Hotel Credit – American Express US"
        />
        <ModuleRoute
          path=":locale/travel/offers/hotels/:page"
          moduleName="axp-travel-marketing-landing-page"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path=":locale/travel/offers/hotels/:page/:filter"
          moduleName="axp-travel-marketing-landing-page"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
        <ModuleRoute
          path="(:locale)/travel/fine-hotels-and-resorts"
          moduleName="axp-travel-luxury-hotels"
          title="Fine Hotels and Resorts"
          taxonomy="FHR"
        />
        <ModuleRoute
          path="(:locale)/travel/the-hotel-collection/book"
          moduleName="axp-travel-luxury-hotels"
          title="The Hotel collection"
          taxonomy="THC"
        />
        <ModuleRoute
          path="(:locale)/travel/travel-guides"
          moduleName="axp-travel-marketing-page"
          title={LOADING_TRAVEL_HOME_TITLE_PLACEHOLDER}
        />
      </ModuleRoute>
      <ModuleRoute
        onEnter={onEnterAuthenticate(store)}
        moduleName="axp-travel-page-wrapper"
        hideGlobalFooter={true}
      >
        <ModuleRoute
          path="(:locale)/travel/select-card"
          moduleName="axp-travel-account-selection"
          title="Card Selection"
        />
      </ModuleRoute>
      <ModuleRoute
        moduleName="axp-travel-page-wrapper"
        hideGlobalFooter={true}
        hideGlobalHeader={true}
        hideTravelNavigation={true}
        onEnter={onEnterAuthenticate(store)}
      >
        <ModuleRoute
          path="(:locale)/travel/search-redirect"
          moduleName="axp-travel-search-redirect"
          title="Session Redirect to Partner"
          isLoginRedirect={true}
        />
        <ModuleRoute
          path="(:locale)/travel/session"
          moduleName="axp-travel-session-redirect-partner"
          title="Session Redirect to Partner"
          onEnter={validatedSessionDestPath}
        />
        <ModuleRoute
          path="(:locale)/travel/mobile-app-search-flights"
          moduleName="axp-travel-search-flights"
          title="Flights: Browse & Book Airline Tickets | American Express Travel"
          theme="white"
        />
        <ModuleRoute
          path="(:locale)/travel/chat-mobile-support"
          component={ClickToChat}
          title="American Express Travel | Mobile Chat Support"
        />
      </ModuleRoute>
    </ModuleRoute>,
    <ModuleRoute onEnter={onEnterLoadIntl(store)} component={ErrorLayout}>
      <ModuleRoute
        code="500"
        component={ErrorMessage}
        path="*/error"
        title="Load Error"
        httpStatus={500}
      />
      <ModuleRoute
        code="404"
        component={ErrorMessage}
        path="*"
        title="Not Found"
        httpStatus={404}
      />
    </ModuleRoute>,
  ];
};

export default childRoutes;
